import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { content } from '../mock';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Phone, Mail, MapPin, Award, Briefcase, GraduationCap, Globe, FileText, Trophy } from 'lucide-react';

const ManagementTeam = () => {
  const { language } = useLanguage();
  const t = content[language];
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  return (
    <section id="management-team" className="py-24 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-20 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-20 w-96 h-96 bg-slate-600/20 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-amber-500/10 backdrop-blur-sm rounded-full mb-6 border border-amber-500/20">
            <span className="text-amber-400 font-semibold text-sm">{t.management.badge}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            {t.management.title}
          </h2>
          <p className="text-lg text-slate-300 max-w-3xl mx-auto leading-relaxed">
            {t.management.subtitle}
          </p>
        </div>

        {/* Profile Card */}
        <div className="max-w-5xl mx-auto">
          <div className="bg-white/5 backdrop-blur-sm rounded-3xl overflow-hidden border border-white/10 hover:border-amber-500/50 transition-all duration-500 shadow-2xl">
            <div className="grid md:grid-cols-5 gap-0">
              {/* Profile Image */}
              <div className="md:col-span-2 relative">
                <div className="relative h-full min-h-[400px] md:min-h-[600px]">
                  <img
                    src="/mohammed-alhabeeb.png"
                    alt="محمد علي محمد الحبيب"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/50 to-transparent"></div>
                  
                  {/* Badge Overlay */}
                  <div className="absolute bottom-6 left-6 right-6">
                    <div className="bg-amber-500/90 backdrop-blur-sm rounded-xl p-4 shadow-xl">
                      <div className="flex items-center gap-3">
                        <Award className="w-6 h-6 text-white" />
                        <div>
                          <div className="text-white font-bold text-sm">
                            {language === 'ar' ? 'محامٍ أمام محكمة التمييز' : 'Attorney Before Court of Cassation'}
                          </div>
                          <div className="text-white/90 text-xs">
                            {language === 'ar' ? 'محكم معتمد' : 'Certified Arbitrator'}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Profile Info */}
              <div className="md:col-span-3 p-8 md:p-12">
                {/* Name & Title */}
                <div className="mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-3">
                    {t.management.profile.name}
                  </h3>
                  <p className="text-amber-400 font-semibold text-lg mb-4">
                    {t.management.profile.title}
                  </p>
                  
                  {/* Contact Info */}
                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-3 text-slate-300">
                      <Phone className="w-4 h-4 text-amber-500" />
                      <a href="tel:+96566669236" className="hover:text-amber-400 transition-colors">
                        +965 66669236
                      </a>
                    </div>
                    <div className="flex items-center gap-3 text-slate-300">
                      <Mail className="w-4 h-4 text-amber-500" />
                      <a href="mailto:M-alhabeeb@hotmail.com" className="hover:text-amber-400 transition-colors break-all">
                        M-alhabeeb@hotmail.com
                      </a>
                    </div>
                    <div className="flex items-center gap-3 text-slate-300">
                      <MapPin className="w-4 h-4 text-amber-500" />
                      <span>{language === 'ar' ? 'دولة الكويت' : 'State of Kuwait'}</span>
                    </div>
                  </div>
                </div>

                {/* Professional Summary */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-amber-500" />
                    {language === 'ar' ? 'النبذة المهنية' : 'Professional Summary'}
                  </h4>
                  <p className="text-slate-300 leading-relaxed whitespace-pre-line">
                    {t.management.profile.summary}
                  </p>
                </div>

                {/* Qualifications - Compact */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-amber-500" />
                    {language === 'ar' ? 'المؤهلات والاعتمادات' : 'Qualifications & Certifications'}
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {t.management.profile.qualifications.map((qual, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <span className="text-amber-500 mt-1">•</span>
                        <span className="text-slate-300 text-sm">{qual}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Areas of Expertise */}
                <div className="mb-8">
                  <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Award className="w-5 h-5 text-amber-500" />
                    {language === 'ar' ? 'مجالات الخبرة' : 'Areas of Expertise'}
                  </h4>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {t.management.profile.expertise.map((area, index) => (
                      <div key={index} className="flex items-start gap-2">
                        <span className="text-amber-500 mt-1">•</span>
                        <span className="text-slate-300 text-sm">{area}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* View Full Profile Button */}
                <Button
                  onClick={() => setIsProfileOpen(true)}
                  className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white py-6 text-lg shadow-xl hover:shadow-2xl transition-all group"
                >
                  <FileText className={`w-5 h-5 ${language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                  {t.management.viewProfileBtn}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Full Profile Modal */}
      <Dialog open={isProfileOpen} onOpenChange={setIsProfileOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">
              {language === 'ar' ? 'السيرة المهنية الكاملة' : 'Full Professional Profile'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-8 py-6">
            {/* Header */}
            <div className="text-center pb-6 border-b">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{t.management.profile.name}</h3>
              <p className="text-amber-600 font-semibold">{t.management.profile.title}</p>
            </div>

            {/* Educational Qualification */}
            <div>
              <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-amber-600" />
                {language === 'ar' ? 'المؤهل العلمي' : 'Educational Qualification'}
              </h4>
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-slate-700">{t.management.fullProfile.education}</p>
              </div>
            </div>

            {/* Professional Registration */}
            <div>
              <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-amber-600" />
                {language === 'ar' ? 'القيد المهني والاعتمادات' : 'Professional Registration & Accreditations'}
              </h4>
              <div className="space-y-2">
                {t.management.fullProfile.registrations.map((reg, index) => (
                  <div key={index} className="flex items-start gap-2 bg-slate-50 rounded-lg p-3">
                    <span className="text-amber-600 mt-1">•</span>
                    <span className="text-slate-700">{reg}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Professional Experience */}
            <div>
              <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-amber-600" />
                {language === 'ar' ? 'الخبرة المهنية' : 'Professional Experience'}
              </h4>
              <div className="space-y-4">
                {t.management.fullProfile.experience.map((exp, index) => (
                  <div key={index} className="bg-slate-50 rounded-xl p-4">
                    <h5 className="font-bold text-slate-900 mb-1">{exp.title}</h5>
                    <p className="text-amber-600 text-sm mb-2">{exp.period}</p>
                    <p className="text-slate-700">{exp.description}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Professional Skills */}
            <div>
              <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-600" />
                {language === 'ar' ? 'المهارات المهنية' : 'Professional Skills'}
              </h4>
              <div className="grid sm:grid-cols-2 gap-2">
                {t.management.fullProfile.skills.map((skill, index) => (
                  <div key={index} className="flex items-center gap-2 bg-slate-50 rounded-lg p-3">
                    <span className="text-amber-600">✓</span>
                    <span className="text-slate-700">{skill}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Languages */}
            <div>
              <h4 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-amber-600" />
                {language === 'ar' ? 'اللغات' : 'Languages'}
              </h4>
              <div className="flex flex-wrap gap-3">
                {t.management.fullProfile.languages.map((lang, index) => (
                  <div key={index} className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                    <span className="text-slate-900 font-semibold">{lang}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ManagementTeam;